import { FC, useState, useRef, useEffect } from 'react'
import { ChevronNextIcon, NotificationIcon } from '@svgs'
import bellEmoji from '@images/emoji/bell.png'
import { Button } from 'src/components/ui'
import NotificationItem, { INotification } from './item/NotificationItem'
import clsx from 'clsx'
import { useNotifications } from 'src/hooks/useNotifications'
import { useTranslation } from 'react-i18next'

interface INotificationsDropdown {}

const NotificationsDropdown: FC<INotificationsDropdown> = () => {
  const { t } = useTranslation()
  const {
    groupedNotifications,
    formatNotificationDate,
    unreadCount,
    notifications,
    observerMarkRef,
    lastElementRef,
  } = useNotifications(10)
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown])

  useEffect(() => {
    const updateBodyOverflow = () => {
      const isMobile = window.matchMedia('(max-width: 991.98px)').matches
      if (showDropdown && isMobile) {
        document.body.classList.add('overflow-hidden')
      } else {
        document.body.classList.remove('overflow-hidden')
      }
    }

    // Initial update
    updateBodyOverflow()

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('overflow-hidden')
    }
  }, [showDropdown])

  return (
    <div className="relative flex flex-col" ref={dropdownRef}>
      <button
        type="button"
        aria-label={t('notifications')}
        aria-expanded={showDropdown}
        onClick={(e) => {
          e.preventDefault()
          setShowDropdown(!showDropdown)
        }}
        className={clsx(
          'relative w-11 h-11 shrink-0 rounded-full border fill-ink border-solid justify-center items-center inline-flex transition-all duration-300 hover:bg-brand-strong hover:border-brand-strong hover:fill-white cursor-pointer',
          showDropdown
            ? 'bg-brand-strong border-brand-strong border-opacity-100 fill-white'
            : 'bg-brand-soft border-ink border-opacity-10',
        )}
      >
        <div className="w-4 h-4 icon-button">
          <NotificationIcon />
        </div>
        {unreadCount > 0 && (
          <div className="absolute -top-2 -right-2 min-w-6 h-6 px-1.5 bg-danger-strong rounded-full text-center flex-col justify-center items-center gap-2 inline-flex text-white text-xs font-bold pointer-events-none">
            {unreadCount}
          </div>
        )}
      </button>

      {showDropdown && (
        <>
          {/* Mobile: under the app header, clear of the notch, never taller
              than the room above the menu (the list inside scrolls) */}
          <div className="fixed top-[calc(var(--header-h)+1rem)] max-md:left-[max(1rem,var(--safe-left))] max-md:right-[max(1rem,var(--safe-right))] max-md:max-h-[calc(100%-var(--header-h)-var(--bottom-bar-h)-2rem)] md:absolute md:right-0 z-50 md:top-full md:mt-2 flex overflow-hidden flex-col gap-4 p-4 py-6 md:p-6 bg-surface rounded-2xl md:w-[33.125rem] shadow-pop">
            <div className="self-center text-lg font-black leading-none text-center text-ink">
              {t('notifications')}
            </div>
            {notifications.length >= 1 ? (
              <>
                <div className="max-h-[60vh] md:max-h-[30.75rem] overflow-y-auto flex flex-col gap-4 pr-4 -mr-4 md:pr-6 md:-mr-6">
                  {Object.entries(groupedNotifications).map(([date, times]) => (
                    <div key={date} className="flex flex-col gap-4">
                      {Object.entries(times).map(([time, notificationsAtTime]) => (
                        <div key={time} className="flex flex-col gap-2">
                          {notificationsAtTime.length > 0 && (
                            <>
                              <div className="text-sm font-extrabold text-ink-2">
                                {formatNotificationDate(notificationsAtTime[0].createdAt)}
                              </div>
                              {notificationsAtTime.map((notification: INotification) => (
                                <div
                                  key={notification.id}
                                  ref={(el) => {
                                    if (el && observerMarkRef.current && !notification.read) {
                                      observerMarkRef.current.observe(el)
                                      el.setAttribute('data-id', notification.id)
                                    }
                                  }}
                                >
                                  <NotificationItem {...notification} />
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                  <div ref={lastElementRef} className="h-1 w-full"></div>
                </div>
                <Button
                  link="/notifications"
                  onClick={() => {
                    setShowDropdown(false)
                  }}
                  className="!h-11 self-center"
                  iconAfter={<ChevronNextIcon />}
                >
                  {t('view_all')}
                </Button>
              </>
            ) : (
              <div className="flex flex-col items-center gap-4 py-8">
                <div className="w-16 h-16 rounded-full bg-brand-soft flex items-center justify-center">
                  <img
                    src={bellEmoji}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                    className="w-9 h-9 object-contain"
                  />
                </div>
                <div className="text-center">
                  <p className="m-0 mb-1 text-base font-extrabold text-ink">
                    {t('notification.empty.title', 'No notifications yet')}
                  </p>
                  <p className="m-0 text-sm text-ink-2">
                    {t('notification.empty.description', 'You will see your notifications here')}
                  </p>
                </div>
                <Button
                  link="/notifications"
                  onClick={() => {
                    setShowDropdown(false)
                  }}
                  className="!h-11"
                  iconAfter={<ChevronNextIcon />}
                >
                  {t('view_all')}
                </Button>
              </div>
            )}
          </div>
          <div
            onClick={() => setShowDropdown(false)}
            className="md:hidden bg-inverse/60 fixed top-[var(--header-h)] inset-x-0 inset-y-0 z-30"
          />
        </>
      )}
    </div>
  )
}

export default NotificationsDropdown
